import type {
  AdminAiScanFeatureReviewGroup,
  AdminAiScanObject,
  AdminAiScanSurfaceReview,
} from "../models/admin-ai-scans.models";
import {
  getAdminAiScanObject,
  getAdminAiScanObjects,
  getAdminAiScanStrings,
  getAdminAiScanText,
} from "./admin-ai-scans.utils";

// Match by stable feature IDs, retaining incomplete partitions rather than claiming reviewed coverage.
const getFeatureCoverage = (id: string, report: AdminAiScanObject | null, review: AdminAiScanObject | null): string => {
  if (!report) {
    return "NOT_SCANNED";
  }

  const partitions = [
    getAdminAiScanStrings(report.reviewedFeatureIds).includes(id) && "reviewed",
    getAdminAiScanStrings(report.weaklyReviewedFeatureIds).includes(id) && "weak",
    getAdminAiScanStrings(report.unreviewedFeatureIds).includes(id) && "unreviewed",
  ].filter(Boolean);

  return partitions.length ? partitions.join(" / ") : getAdminAiScanText(review?.reviewDepth, "COVERAGE_UNSPECIFIED");
};

// Use inventory surfaces for ordering while preserving orphan reviews and findings in an explicit group.
// A multi-feature finding is shown once at its first matching group, with all references still readable.
export const getAdminAiScanReviewSurfaces = (
  inventory: AdminAiScanObject | null,
  report: AdminAiScanObject | null,
  featureReviewKey: string,
): { surfaces: AdminAiScanSurfaceReview[]; unassignedFindings: AdminAiScanObject[] } => {
  const reviews =
    featureReviewKey === "boReview"
      ? getAdminAiScanObjects(getAdminAiScanObject(report?.boReview).featureGroups)
      : getAdminAiScanObjects(report?.[featureReviewKey]);
  const reviewById = new Map(reviews.map((review) => [getAdminAiScanText(review.id), review]));
  const groupsById = new Map<string, AdminAiScanFeatureReviewGroup>();
  const surfaces: AdminAiScanSurfaceReview[] = getAdminAiScanObjects(inventory?.surfaces).map((surface) => ({
    id: getAdminAiScanText(surface.id),
    label: getAdminAiScanText(surface.label, getAdminAiScanText(surface.id)),
    kind: getAdminAiScanText(surface.kind),
    groups: getAdminAiScanObjects(surface.featureGroups).map((group) => {
      const id = getAdminAiScanText(group.id);
      const review = reviewById.get(id) ?? null;
      const entry = {
        id,
        label: getAdminAiScanText(group.label, id),
        inventory: group,
        review,
        coverage: getFeatureCoverage(id, report, review),
        findings: [],
      };
      groupsById.set(id, entry);

      return entry;
    }),
  }));
  const orphanGroups: AdminAiScanFeatureReviewGroup[] = [];
  const referencedIds = [
    ...getAdminAiScanStrings(getAdminAiScanObject(report?.target).featureGroupIds),
    ...reviews.map((review) => getAdminAiScanText(review.id)),
    ...getAdminAiScanStrings(report?.reviewedFeatureIds),
    ...getAdminAiScanStrings(report?.weaklyReviewedFeatureIds),
    ...getAdminAiScanStrings(report?.unreviewedFeatureIds),
  ];

  referencedIds.forEach((id) => {
    if (!id || groupsById.has(id)) {
      return;
    }

    const review = reviewById.get(id) ?? null;
    const entry = {
      id,
      label: id,
      inventory: null,
      review,
      coverage: getFeatureCoverage(id, report, review),
      findings: [],
    };
    groupsById.set(id, entry);
    orphanGroups.push(entry);
  });

  if (orphanGroups.length) {
    surfaces.push({
      id: "outside-inventory",
      label: "Outside current feature inventory",
      kind: "",
      groups: orphanGroups,
    });
  }

  const unassignedFindings: AdminAiScanObject[] = [];
  getAdminAiScanObjects(report?.findings).forEach((finding) => {
    const affectedIds = getAdminAiScanStrings(finding.affectedFeatureIds);
    const group =
      affectedIds.map((id) => groupsById.get(id)).find(Boolean) ??
      Array.from(groupsById.values()).find((entry) =>
        getAdminAiScanStrings(entry.review?.issueIds).includes(getAdminAiScanText(finding.id)),
      );

    if (group) {
      group.findings.push(finding);
    } else {
      unassignedFindings.push(finding);
    }
  });

  return { surfaces, unassignedFindings };
};
